import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Box } from 'rebass/styled-components';
import { FolderOpen, Contact, Folder, Building2, Pencil, Trash, Plus, FolderPlus, Copy } from 'lucide-react';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/list-item';
import { DeleteSharedListModal } from 'components/atoms/deleteSharedListModal';
import { DuplicateSharedListModal } from 'components/molecules/sharedListsActions/components/DuplicateSharedListModal';
import TreeItemRow from './TreeItemRow';
import DeleteFolderModal from './DeleteFolderModal';
import FolderNameModal from './FolderNameModal';
import { useTreeNode } from './hooks/useTreeNode';

const INDENT_PER_LEVEL = 14;

export function TreeNode({
  item,
  level,
  slug,
  isExpandedLookup,
  toggleFolder,
  parentIndex,
  tree,
  expandFolder,
  moveListToFolder,
  moveFolder,
  updateFolder,
  createFolder,
  deleteSharedList,
  deletingList,
  duplicateSharedList,
  duplicatingList,
  deleteFolder,
  deletingFolder,
  selectedListId,
  activeFolderIds,
  networkId,
}) {
  const {
    isExpanded,
    rowRef,
    instruction,
    openDeleteList,
    openDuplicateList,
    openDeleteFolder,
    openRenameFolder,
    openCreateFolder,
    handleRowClick,
    handleOpenDeleteList,
    handleCloseDeleteList,
    handleConfirmDeleteList,
    handleOpenDuplicateList,
    handleCloseDuplicateList,
    handleConfirmDuplicateList,
    handleOpenDeleteFolder,
    handleCloseDeleteFolder,
    handleConfirmDeleteFolder,
    handleOpenRenameFolder,
    handleCloseRenameFolder,
    handleSubmitRenameFolder,
    handleOpenCreateFolder,
    handleCloseCreateFolder,
    handleSubmitCreateFolder,
    handleCreateListInFolder,
  } = useTreeNode({
    item,
    level,
    slug,
    isExpandedLookup,
    toggleFolder,
    parentIndex,
    tree,
    expandFolder,
    moveListToFolder,
    moveFolder,
    updateFolder,
    createFolder,
    deleteSharedList,
    duplicateSharedList,
    deleteFolder,
    selectedListId,
    networkId,
  });

  if (item.type === 'list') {
    const listIcon = item.kind === 'people' ? Contact : Building2;
    const actions = [];

    // Add duplicate action only for people lists
    if (item.kind === 'people') {
      actions.push({
        text: 'Duplicate',
        icon: Copy,
        onClick: handleOpenDuplicateList,
      });
    }

    // Add delete action
    actions.push({
      text: 'Delete',
      icon: Trash,
      sx: { color: 'text.error' },
      iconSx: { color: 'text.error' },
      onClick: handleOpenDeleteList,
    });

    return (
      <Box key={`list-${item.id}`} ref={rowRef} sx={{ position: 'relative' }}>
        {instruction && !instruction.blocked ? <DropIndicator instruction={instruction} /> : null}
        <TreeItemRow
          leftIcon={listIcon}
          indentPx={level * INDENT_PER_LEVEL}
          label={item.name}
          href={`/networks/${slug}/list/${item.id}/contacts`}
          isActive={selectedListId === item.id}
          actions={actions}
          instruction={instruction}
        />
        {openDeleteList &&
          createPortal(
            <DeleteSharedListModal
              list={{ id: item.id, name: item.name }}
              isLoading={deletingList}
              onCloseModal={handleCloseDeleteList}
              onDeleteSharedList={handleConfirmDeleteList}
            />,
            document.getElementById('portals'),
          )}
        {openDuplicateList &&
          createPortal(
            <DuplicateSharedListModal
              list={{ id: item.id, name: item.name }}
              isLoading={duplicatingList}
              onCloseModal={handleCloseDuplicateList}
              onDuplicateSharedList={handleConfirmDuplicateList}
            />,
            document.getElementById('portals'),
          )}
      </Box>
    );
  }

  return (
    <Box key={`folder-${item.id}`}>
      <Box ref={rowRef} sx={{ position: 'relative' }}>
        {instruction && !instruction.blocked ? <DropIndicator instruction={instruction} /> : null}
        <TreeItemRow
          leftIcon={isExpanded ? FolderOpen : Folder}
          indentPx={level * INDENT_PER_LEVEL}
          label={item.name}
          onRowClick={handleRowClick}
          actions={[
            {
              text: 'Rename',
              icon: Pencil,
              onClick: handleOpenRenameFolder,
            },
            { divider: true },
            {
              text: 'Delete',
              icon: Trash,
              sx: { color: 'text.error' },
              iconSx: { color: 'text.error' },
              onClick: handleOpenDeleteFolder,
            },
          ]}
          createActions={[
            {
              text: 'Create list',
              icon: Plus,
              onClick: handleCreateListInFolder,
            },
            {
              text: 'Create folder',
              icon: FolderPlus,
              onClick: () => {
                expandFolder(item.id);
                handleOpenCreateFolder();
              },
            },
          ]}
          instruction={instruction}
        />
        {openDeleteFolder &&
          createPortal(
            <DeleteFolderModal
              folder={{ id: item.id, name: item.name }}
              isLoading={deletingFolder}
              onCloseModal={handleCloseDeleteFolder}
              onDeleteFolder={handleConfirmDeleteFolder}
            />,
            document.getElementById('portals'),
          )}
        {openRenameFolder &&
          createPortal(
            <FolderNameModal
              title="Rename folder"
              label="Folder name"
              initialValue={item.name}
              confirmText="Update"
              isLoading={false}
              onCancel={handleCloseRenameFolder}
              onSubmit={handleSubmitRenameFolder}
            />,
            document.getElementById('portals'),
          )}
        {openCreateFolder &&
          createPortal(
            <FolderNameModal
              title="Create folder"
              label="New folder name"
              confirmText="Create folder"
              isLoading={false}
              onCancel={handleCloseCreateFolder}
              onSubmit={handleSubmitCreateFolder}
            />,
            document.getElementById('portals'),
          )}
      </Box>
      {isExpanded &&
        item.children?.map((child) => (
          <TreeNode
            key={`${child.type}-${child.id}`}
            item={child}
            level={level + 1}
            slug={slug}
            isExpandedLookup={isExpandedLookup}
            toggleFolder={toggleFolder}
            parentIndex={parentIndex}
            tree={tree}
            expandFolder={expandFolder}
            moveListToFolder={moveListToFolder}
            moveFolder={moveFolder}
            createFolder={createFolder}
            updateFolder={updateFolder}
            deleteSharedList={deleteSharedList}
            deletingList={deletingList}
            duplicateSharedList={duplicateSharedList}
            duplicatingList={duplicatingList}
            deleteFolder={deleteFolder}
            deletingFolder={deletingFolder}
            selectedListId={selectedListId}
            activeFolderIds={activeFolderIds}
            networkId={networkId}
          />
        ))}
    </Box>
  );
}

TreeNode.propTypes = {
  item: PropTypes.object.isRequired,
  level: PropTypes.number.isRequired,
  slug: PropTypes.string.isRequired,
  isExpandedLookup: PropTypes.func.isRequired,
  toggleFolder: PropTypes.func.isRequired,
  parentIndex: PropTypes.object.isRequired,
  tree: PropTypes.array.isRequired,
  expandFolder: PropTypes.func.isRequired,
  moveListToFolder: PropTypes.func.isRequired,
  moveFolder: PropTypes.func.isRequired,
  createFolder: PropTypes.func.isRequired,
  updateFolder: PropTypes.func.isRequired,
  deleteSharedList: PropTypes.func.isRequired,
  deletingList: PropTypes.bool.isRequired,
  duplicateSharedList: PropTypes.func.isRequired,
  duplicatingList: PropTypes.bool.isRequired,
  deleteFolder: PropTypes.func.isRequired,
  deletingFolder: PropTypes.bool.isRequired,
  selectedListId: PropTypes.number,
  activeFolderIds: PropTypes.object,
  networkId: PropTypes.number.isRequired,
};

TreeNode.defaultProps = {
  selectedListId: undefined,
  activeFolderIds: undefined,
};
